import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { Printer } from "@phosphor-icons/react";

const STATUSES = ["placed", "in_production", "quality_check", "dispatched", "delivered"];

export default function VendorDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/vendor/orders").then(r => setOrders(r.data)).finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const update = async (id, s) => {
    try {
      await api.patch(`/vendor/orders/${id}/status`, { status: s });
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const active = orders.filter(o => !["delivered","cancelled"].includes(o.status));
  const done = orders.filter(o => ["delivered","cancelled"].includes(o.status));

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="pf-label mb-3">Vendor · Workspace</div>
        <h1 className="text-5xl font-black tracking-tighter mb-10 flex items-center gap-4">
          <Printer size={48} weight="duotone" /> Your orders.
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[var(--pf-border)] mb-10">
          {[
            ["Active", active.length],
            ["In production", orders.filter(o => o.status === "in_production").length],
            ["Delivered", orders.filter(o => o.status === "delivered").length],
            ["Total value", `₹${orders.reduce((s,o)=>s+(o.total_amount||0),0).toLocaleString()}`],
          ].map(([l,v]) => (
            <div key={l} className="border-r border-b border-[var(--pf-border)] p-6" data-testid={`v-stat-${l.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="pf-label">{l}</div>
              <div className="font-mono text-3xl font-black mt-2">{v}</div>
            </div>
          ))}
        </div>

        <section className="mb-12">
          <div className="pf-label mb-2">Section · Active production</div>
          <h2 className="text-3xl font-black tracking-tight mb-6">Assigned orders</h2>
          {loading ? <div className="pf-label">Loading…</div> : active.length === 0 ? (
            <div className="border border-dashed border-[var(--pf-border)] p-16 text-center text-[var(--pf-text-2)]" data-testid="no-orders">
              No orders assigned. Admin will route jobs here when your capability matches.
            </div>
          ) : (
            <div className="border-2 border-[var(--pf-brand)]">
              {active.map(o => (
                <div key={o.id} className="grid grid-cols-12 gap-4 items-center p-5 border-b last:border-0 border-[var(--pf-border)]" data-testid={`v-order-${o.id}`}>
                  <div className="col-span-3">
                    <div className="font-mono text-xs text-[var(--pf-text-2)]">#{o.id.slice(0,8).toUpperCase()}</div>
                    <div className="font-bold mt-1 capitalize">{o.product_type.replace('_',' ')}</div>
                    <div className="text-xs text-[var(--pf-text-2)] mt-1">{new Date(o.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="col-span-3 font-mono">
                    <div className="font-semibold">{o.quantity?.toLocaleString()} pcs</div>
                    <div className="text-xs text-[var(--pf-text-2)] mt-1">₹{o.total_amount?.toLocaleString()}</div>
                  </div>
                  <div className="col-span-3 text-sm text-[var(--pf-text-2)]">{o.delivery_address}</div>
                  <div className="col-span-3">
                    <select value={o.status} onChange={(e)=>update(o.id, e.target.value)} data-testid={`v-status-${o.id}`}
                      className="border-2 border-[var(--pf-border)] px-3 py-2 text-xs uppercase tracking-widest font-bold bg-white w-full">
                      {STATUSES.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {done.length > 0 && (
          <section>
            <div className="pf-label mb-2">Section · Archive</div>
            <h2 className="text-3xl font-black tracking-tight mb-6">Completed</h2>
            <div className="border border-[var(--pf-border)]">
              {done.map(o => (
                <div key={o.id} className="grid grid-cols-12 gap-4 items-center p-4 border-b last:border-0 border-[var(--pf-border)]">
                  <div className="col-span-5 font-semibold">#{o.id.slice(0,8).toUpperCase()} · {o.product_type.replace('_',' ')}</div>
                  <div className="col-span-3 font-mono">{o.quantity} · ₹{o.total_amount}</div>
                  <div className="col-span-4 text-right">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[var(--pf-surface-2)]">{o.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
